import React, { useState, useEffect } from 'react';
import { Box, Button, Card, CardContent, CardMedia, Container, Typography } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useTranslation } from 'react-i18next';
import { useLocalizedField } from '../../hooks/useLocalizedField';

interface Service {
  id: string;
  name: string;
  description: string;
  imageUrl: string;
  duration: number;
  price: { min: number; max: number };
  category: string;
  popular: boolean;
  showPrice?: boolean;
  translations?: {
    fr?: { name?: string; description?: string };
    es?: { name?: string; description?: string };
  };
}

const FeaturedServices: React.FC = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const { getField } = useLocalizedField();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const servicesRef = collection(db, 'services');
        const popularServicesQuery = query(servicesRef, where('popular', '==', true));
        const querySnapshot = await getDocs(popularServicesQuery);

        const servicesData: Service[] = [];
        querySnapshot.forEach((doc) => {
          servicesData.push({
            id: doc.id,
            ...doc.data()
          } as Service);
        });

        setServices(servicesData);
      } catch (error) {
        console.error('Error fetching services:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  if (loading) {
    return (
      <Box sx={{ py: 8, bgcolor: 'background.default' }}>
        <Container>
          <Typography variant="h4" align="center">
            {t('featuredServices.loading')}
          </Typography>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 8, bgcolor: 'background.default' }}>
      <Container>
        <Typography
          variant="h2"
          component="h2"
          align="center"
          sx={{ mb: 2, fontWeight: 600 }}
        >
          {t('featuredServices.title')}
        </Typography>
        <Typography
          variant="h6"
          component="p"
          align="center"
          color="text.secondary"
          sx={{ mb: 6, maxWidth: 800, mx: 'auto' }}
        >
          {t('featuredServices.subtitle')}
        </Typography>

        <Box sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)'
          },
          gap: 4,
          mb: 4
        }}>
          {services.map((service: Service) => (
            <Box key={service.id}>
              <Card
                onClick={() => navigate('/booking', { state: { service } })}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 20px rgba(0, 0, 0, 0.1)',
                  }
                }}
              >
                <CardMedia
                  component="img"
                  height="200"
                  image={service.imageUrl}
                  alt={service.name}
                  sx={{ objectFit: 'cover' }}
                />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h5" component="h3" gutterBottom>
                    {getField(service, 'name', service.name)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {getField(service, 'description', service.description)}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                    {service.showPrice !== false ? (
                      <Typography variant="body1" fontWeight={500}>
                        ${service.price.min} - ${service.price.max}
                      </Typography>
                    ) : (
                      <Box />
                    )}
                    <Typography variant="body2" color="text.secondary">
                      {service.duration} {t('common.min')}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>

        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Button
            component={RouterLink}
            to="/services"
            variant="contained"
            color="secondary"
            size="large"
          >
            {t('featuredServices.viewAll')}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default FeaturedServices;
